/* Cotton purchases — the bills register, the month in focus.
 *
 * Bales by month as bars with the weighted ₹/candy as a line over them, the
 * month's suppliers ranked, and every bill it holds. ₹/candy is value ÷ kg
 * × 356 throughout, the app's one definition of a candy (the desktop pages
 * and services/units.py agree; the original mockup used 355.62).
 */

import type { MobileSummary } from '../../lib/api_mobile'
import { MobileTable, type MRow } from '../MobileTable'
import {
  Bars, CR, Head, IN, KpiGrid, LK, NoteBox, RS, SG, TN, kpi, Rank, type MBar,
} from '../parts'

export function Purchases({ data }: { data: MobileSummary }) {
  const p = data.purch
  if (p.empty || !p.months.length) {
    return <NoteBox>No purchase bills on file. Load the bills register on the
      web app's Data loads page.</NoteBox>
  }

  const months = p.months
  const focus = months.find((m) => m.mk === p.mk) ?? months[months.length - 1]
  const prevIndex = months.indexOf(focus) - 1
  const prev = prevIndex >= 0 ? months[prevIndex] : null
  const short = p.label.slice(0, 3)

  const fyBales = months.reduce((s, m) => s + m.bales, 0)
  const fyValue = months.reduce((s, m) => s + m.v, 0)

  const maxB = Math.max(1, ...months.map((m) => m.bales))
  const candies = months.map((m) => m.candy).filter((c): c is number => c != null)
  const cLo = candies.length ? Math.min(...candies) * 0.97 : 0
  const cHi = candies.length ? Math.max(...candies) * 1.01 : 1
  // The bars use the lower 56% of the plot and the price line the upper
  // band, so the two series share a frame without either being squashed.
  const bars: MBar[] = months.map((m) => ({
    h: m.bales / maxB * 56,
    c: m.mk === p.mk ? 'var(--c-ink)' : 'var(--c-bar1)',
    top: IN(m.bales),
    lab: m.lab,
  }))
  const linePoints = months.map((m) =>
    (m.candy == null ? 74 : 74 + (m.candy - cLo) / ((cHi - cLo) || 1) * 22))

  const maxSup = Math.max(1, ...p.suppliers.map((s) => s.bales))

  const bills: MRow[] = p.bills.map((b) => ({
    cells: [
      { v: b.sup, sub: b.type },
      { v: `${b.d} ${short}`, s: Number(b.d) },
      { v: String(b.bales) },
      { v: IN(b.kg) },
      { v: IN(b.candy), s: b.candy ?? 0 },
      { v: LK(b.v), s: b.v },
      { v: b.st },
    ],
  }))

  return (
    <>
      <div className="m-label" style={{ padding: '12px 0 2px' }}>
        Purchase bills · {p.label} in focus
      </div>

      <KpiGrid style={{ marginTop: 16 }} items={[
        kpi(`Bales · ${short}`, IN(focus.bales), `${focus.n} bills · ${TN(focus.kg)}`),
        kpi(`Value · ${short}`, CR(focus.v), 'taxable, before GST'),
        kpi('Avg ₹ / candy', RS(focus.candy),
          prev?.candy != null && focus.candy != null
            ? `${SG(focus.candy - prev.candy, IN)} vs ${prev.lab}`
            : 'no earlier month to compare',
          !!(prev?.candy != null && focus.candy != null && focus.candy > prev.candy)),
        kpi('FY to date', `${IN(fyBales)} bales`, CR(fyValue)),
      ]} />

      <Head title="Bales bought by month" aside="bales · ₹/candy" />
      <div className="m-aside" style={{ marginTop: -6, marginBottom: 12 }}>
        The current month is part-month. Price is value ÷ kg × {data.candyKg}.
      </div>
      <Bars bars={bars} gap={10}
        line={candies.length
          ? { points: linePoints, label: `${RS(Math.max(...candies))} peak` }
          : undefined}
        legend={[
          { t: 'Bales', c: 'var(--c-bar1)' },
          { t: short, c: 'var(--c-ink)' },
          { t: 'Avg ₹ / candy', border: '2px solid var(--c-good)', line: true },
        ]} />

      {p.suppliers.length > 0 && (
        <>
          <Head title={`Suppliers · ${short}`} aside={`${p.suppliers.length}`} />
          <Rank items={p.suppliers.slice(0, 6).map((s) => ({
            l: s.s,
            v: `${IN(s.bales)} bales · ${CR(s.v)}`,
            w: s.bales / maxSup * 100,
          }))} />
        </>
      )}

      <Head title={`Bills · ${short}`} aside={`${p.bills.length} bills`} />
      <MobileTable cols={[
        { l: 'Supplier', w: 150 }, { l: 'Date', w: 62 }, { l: 'Bales', w: 54 },
        { l: 'Kg', w: 70 }, { l: '₹ / candy', w: 80 }, { l: 'Value', w: 76 },
        { l: 'Station', w: 120, al: 'left' },
      ]} rows={bills} />
    </>
  )
}
